import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Bed, Lightbulb, Music, Tv, Wind, Shield, Wifi, Coffee, Droplet, Star, ChevronRight, Sparkles, Heart, Wine,
  Users, Maximize, LogIn, LogOut, Clock, Moon, ArrowUpRight,
} from "lucide-react";
import api, { formatINR, mediaUrl } from "../lib/api";
import { motion, Reveal, StaggerContainer, staggerItem, easeLuxury } from "../components/motion";
import AirbnbLink from "../components/AirbnbLink";
import { useContent } from "../context/SiteContentContext";

const ICONS = { bed: Bed, lightbulb: Lightbulb, music: Music, tv: Tv, wind: Wind, shield: Shield, wifi: Wifi, coffee: Coffee, droplet: Droplet, sparkles: Sparkles, heart: Heart, wine: Wine };


// Bento gallery: [src, label, grid-span classes]
const GALLERY = [
  ["/Images/suite1-hero.png", "The Suite", "md:col-span-2 md:row-span-2"],
  ["/Images/lounge2.png", "The Lounge", ""],
  ["/Images/washroom2.png", "The Sanctuary", ""],
  ["/Images/kitchen3.png", "The Kitchenette", ""],
  ["/Images/suite2-angle2.png", "Evening light", ""],
  ["/Images/suite1-detail.png", "Details", "md:col-span-2"],
];

function Fact({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-4 px-6 py-5">
      <div className="w-11 h-11 border border-gold/30 flex items-center justify-center shrink-0"><Icon size={17} className="text-gold" /></div>
      <div>
        <div className="font-display text-xl text-white leading-none">{value}</div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-2">{label}</div>
      </div>
    </div>
  );
}

export default function RoomPage() {
  const { get } = useContent();
  const [info, setInfo] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [prop, setProp] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [rules, setRules] = useState(null);

  useEffect(() => {
    api.get("/room/info").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/room/pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/properties").then((r) => setProp(r.data[0] || null)).catch(() => {});
    api.get("/amenities").then((r) => setAmenities(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/reviews").then((r) => setReviews(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/rules").then((r) => setRules(r.data)).catch(() => {});
  }, []);

  const t = prop?.timings;
  const hourly = prop ? prop.hourly_rate : pricing?.hourly_rate;
  const nightly = prop ? prop.nightly_rate : pricing?.overnight_rate;
  const description = get("room.description");
  const propertyRules = rules?.rules?.property_rules?.length ? rules.rules.property_rules : [
    "Couples only — both guests must check in together with valid ID.",
    "No smoking inside the room.",
    "Respect quiet hours after 11 PM.",
    "Any damages will be charged as per policy.",
  ];

  return (
    <div className="grain pb-24 lg:pb-0" data-testid="room-page" style={{ background: "var(--bg-base)" }}>
      {/* ───────── Hero ───────── */}
      <section className="relative pt-28 sm:pt-32 pb-16 overflow-hidden">
        <div className="absolute pointer-events-none" aria-hidden="true"
          style={{ top: "10%", left: "-10%", width: 700, height: 600, background: "radial-gradient(ellipse, rgba(155,27,27,0.22), transparent 70%)", filter: "blur(60px)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: easeLuxury }}>
            <div className="eyebrow">The Room</div>
            <h1 className="font-cormorant mt-5 leading-[0.95]" style={{ fontSize: "clamp(3.2rem, 9vw, 6.5rem)" }}>
              <span className="text-gold">{(info?.name || "ROOM XIII").split(" ")[0]}</span>{" "}
              <span className="italic text-crimson-bright">{(info?.name || "ROOM XIII").split(" ").slice(1).join(" ")}</span>
            </h1>
            <div className="divider-gold" />
            <p className="text-luxury-body text-base sm:text-lg max-w-xl">{description}</p>

            <div className="flex flex-wrap gap-x-10 gap-y-4 mt-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">From</div>
                <div className="font-display text-3xl text-gold mt-1">{hourly ? formatINR(hourly) : "—"}<span className="text-sm text-white/40 font-sans"> / hour</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Overnight</div>
                <div className="font-display text-3xl text-gold mt-1">{nightly ? formatINR(nightly) : "—"}<span className="text-sm text-white/40 font-sans"> / night</span></div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/booking" className="btn-gold inline-flex items-center gap-2" data-testid="room-book-now">Reserve The Room <ChevronRight size={15} /></Link>
              <a href="#gallery" className="btn-ghost inline-flex items-center gap-2">View Gallery</a>
              <AirbnbLink url={prop?.airbnb_url} className="btn-ghost" testid="room-airbnb-link" />
            </div>
          </motion.div>

          <motion.div className="relative" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.15, ease: easeLuxury }}>
            <div className="absolute -inset-3 border border-gold/20 translate-x-4 translate-y-4 pointer-events-none" aria-hidden="true" />
            <div className="relative overflow-hidden aspect-[4/5] sm:aspect-[5/6]">
              <img src={mediaUrl(get("room.hero_image"))} alt="ROOM XIII private suite" className="w-full h-full object-cover animate-ken-burns" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(7,6,5,0.85))" }} />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div className="text-white/80 text-sm font-cormorant italic text-xl">{get("room.hero_caption")}</div>
                <div className="flex items-center gap-1 text-gold text-sm"><Star size={13} className="fill-gold" /> 5.0</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────── Quick facts ───────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <div className="glass-strong grid grid-cols-2 lg:grid-cols-5 divide-white/[0.06] lg:divide-x border border-gold/10" data-testid="room-facts">
            <Fact icon={Users} value={`${prop?.max_guests ?? 2} guests`} label="Couples only" />
            <Fact icon={Maximize} value={`${get("room.area_sqft")} sq ft`} label="Private suite" />
            <Fact icon={Bed} value={get("room.beds")} label="Premium linen" />
            <Fact icon={LogIn} value={t ? `From ${t.check_in_label}` : "—"} label="Check-in" />
            <Fact icon={LogOut} value={t ? `Before ${t.check_out_label}` : "—"} label="Check-out" />
          </div>
        </Reveal>
      </section>

      {/* ───────── Gallery (bento) ───────── */}
      <section id="gallery" className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <Reveal>
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="eyebrow">Inside</div>
              <h2 className="font-cormorant text-4xl sm:text-5xl mt-4">Every corner, <span className="italic text-crimson-bright">composed.</span></h2>
            </div>
            <Link to="/gallery" className="hidden sm:inline-flex items-center gap-1 text-gold text-sm hover:underline">Full gallery <ArrowUpRight size={14} /></Link>
          </div>
        </Reveal>
        <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 auto-rows-[170px] sm:auto-rows-[210px] gap-3 sm:gap-4" stagger={0.09}>
          {GALLERY.map(([src, label, span]) => (
            <motion.figure key={label} variants={staggerItem} className={`group relative overflow-hidden m-0 ${span}`}>
              <img src={src} alt={`${label} at ROOM XIII`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 opacity-70 group-hover:opacity-40 transition-opacity" style={{ background: "linear-gradient(180deg, transparent 50%, rgba(7,6,5,0.9))" }} />
              <figcaption className="absolute bottom-3 left-4 font-cormorant text-xl text-white">{label}</figcaption>
            </motion.figure>
          ))}
        </StaggerContainer>
      </section>

      {/* ───────── Details + pricing ───────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="grid lg:grid-cols-3 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-2 space-y-20">
            {/* Amenities */}
            <div>
              <Reveal>
                <div className="eyebrow mb-3">In-room</div>
                <h2 className="font-cormorant text-4xl mb-8">Amenities</h2>
              </Reveal>
              {amenities.length === 0 ? (
                <div className="grid sm:grid-cols-2 gap-4" aria-busy="true">{[...Array(4)].map((_, i) => <div key={i} className="glass h-24 animate-pulse" />)}</div>
              ) : (
                <StaggerContainer className="grid sm:grid-cols-2 gap-4" stagger={0.05}>
                  {amenities.map((a) => {
                    const Icon = ICONS[a.icon] || Star;
                    return (
                      <motion.div key={a.id ?? a.title} variants={staggerItem}
                        className="flex gap-4 items-start p-5 border border-white/[0.06] hover:border-gold/30 hover:bg-white/[0.02] transition-colors">
                        <div className="w-10 h-10 border border-gold/25 flex items-center justify-center shrink-0"><Icon className="text-gold" size={16} /></div>
                        <div>
                          <div className="text-white">{a.title || a.name}</div>
                          <div className="text-xs text-white/45 mt-1 leading-relaxed">{a.description}</div>
                        </div>
                      </motion.div>
                    );
                  })}
                </StaggerContainer>
              )}
            </div>

            {/* Rules */}
            <Reveal>
              <div className="eyebrow mb-3">Good to know</div>
              <h2 className="font-cormorant text-4xl mb-8">House rules</h2>
              <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-4 text-white/65 text-sm">
                {propertyRules.map((rule) => (
                  <li key={rule} className="flex items-start gap-3"><span className="text-gold mt-0.5">—</span>{rule}</li>
                ))}
              </ul>
              <Link to="/rules" className="inline-flex items-center gap-1 text-gold text-sm mt-8 hover:underline">Read check-in, check-out & cancellation rules <ArrowUpRight size={14} /></Link>
            </Reveal>

            {/* Reviews */}
            {reviews.length > 0 && (
              <div>
                <Reveal>
                  <div className="eyebrow mb-3">Guest reviews</div>
                  <h2 className="font-cormorant text-4xl mb-8">Loved by couples</h2>
                </Reveal>
                <StaggerContainer className="grid md:grid-cols-2 gap-5" stagger={0.1}>
                  {reviews.slice(0, 4).map((r) => (
                    <motion.blockquote key={r.id} variants={staggerItem} className="glass p-6 m-0">
                      <div className="flex gap-1 mb-3">{Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={12} className="text-gold fill-gold" />)}</div>
                      <div className="font-cormorant text-2xl text-white">{r.title}</div>
                      <p className="text-white/55 text-sm mt-2 leading-relaxed">{r.comment}</p>
                      <footer className="text-xs text-gold mt-4 uppercase tracking-[0.2em]">— {r.user_name}</footer>
                    </motion.blockquote>
                  ))}
                </StaggerContainer>
              </div>
            )}
          </div>

          {/* Sticky pricing card */}
          <aside className="lg:sticky lg:top-28" aria-label="Pricing">
            <Reveal>
              <div className="glass-strong p-7 border border-gold/20 spin-border">
                <div className="eyebrow mb-5">Pricing</div>
                <div className="space-y-5">
                  <div className="flex items-center justify-between border-b border-white/[0.06] pb-5">
                    <div className="flex items-center gap-3"><Clock size={16} className="text-gold" /><div><div className="text-white">Hourly</div><div className="text-xs text-white/40">{prop ? `${prop.min_hours}–${prop.max_hours} hours` : ""}</div></div></div>
                    <div className="font-display text-2xl text-gold">{hourly ? formatINR(hourly) : "—"}</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3"><Moon size={16} className="text-gold" /><div><div className="text-white">Overnight</div><div className="text-xs text-white/40">per night</div></div></div>
                    <div className="font-display text-2xl text-gold">{nightly ? formatINR(nightly) : "—"}</div>
                  </div>
                </div>
                {t && (
                  <div className="grid grid-cols-2 gap-3 mt-6 text-xs">
                    <div className="border border-white/10 p-3"><div className="text-white/40 uppercase tracking-[0.18em]">Check-in</div><div className="text-white mt-1">From {t.check_in_label}</div></div>
                    <div className="border border-white/10 p-3"><div className="text-white/40 uppercase tracking-[0.18em]">Check-out</div><div className="text-white mt-1">Before {t.check_out_label}</div></div>
                  </div>
                )}
                {prop?.cancellation && (
                  <p className="text-xs text-white/45 mt-5 leading-relaxed">
                    {prop.cancellation.refund_type === "NON_REFUNDABLE" ? "Non-refundable rate." : prop.cancellation.name + " cancellation policy."} Payment is collected at the property.
                  </p>
                )}
                <Link to="/booking" className="btn-gold w-full mt-6 text-center flex items-center justify-center gap-2">Book Now <ChevronRight size={15} /></Link>
                <AirbnbLink url={prop?.airbnb_url} className="btn-ghost w-full mt-3" testid="room-card-airbnb-link" />
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      {/* Mobile booking bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-[#0b0908]/95 backdrop-blur px-5 py-3 flex items-center justify-between gap-4">
        <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/40">From</div><div className="font-display text-xl text-gold">{hourly ? `${formatINR(hourly)}` : "—"}<span className="text-xs text-white/40 font-sans"> / hr</span></div></div>
        <Link to="/booking" className="btn-gold">Book Now</Link>
      </div>
    </div>
  );
}
